// Toolbar UI — 1:1 port of the live header (snapshot L648-807) and secondary
// toolbar (snapshot L810-1027) into the dark-slate theme. Built with the shared
// ui/dom.ts + ui/icons.ts helpers; handlers are wired via addEventListener.
//
// buildHeader()           fills #toolbar  (header  h-11 bg-slate-900)
// buildSecondaryToolbar() fills #toolbar2 (toolbar h-10 bg-slate-800)
//
// Both are invoked once from main.ts boot(). Re-running rebuilds the bar.

import { Group, ActiveSelection } from 'fabric'
import { el, dropdown, type DropdownItem } from './dom'
import { icon } from './icons'
import { state } from '../state'

import { undo, redo } from '../history'
import { zoomIn, zoomOut, fitToView, resizeCanvas } from '../canvas'
import { openSizePicker } from '../panels/sizePicker'
import { updateStatus } from '../statusbar'

import { addText } from '../widgets/text'
import { addShape } from '../widgets/shapes'
import { addBarcode } from '../widgets/barcode'
import { addQRCode } from '../widgets/qrcode'
import { addImageFromFile } from '../widgets/image'
import { addTable } from '../widgets/table-widget'
import { addDynamicTable } from '../widgets/dynamic-table'
import { duplicateSelected, deleteSelected } from '../clipboard'
import { gated } from '../ads/gate'

import { openBatchModal, insertFieldOnCanvas } from '../batch/ui'
import { openExportModal } from '../export'
import { cloudSaveDesign } from '../cloud/save'
import { saveOlmDesign, openOlmDesign } from '../olmfile'
import { openStartScreen } from '../start'
import { PRODUCT, logoHtml } from '../product'
import { openAuth } from '../auth/auth-ui'
import { signOut } from '../auth/supabase'
import { openDesignsModal } from '../cloud/designs'

// ---------------------------------------------------------------------------
// Small builders
// ---------------------------------------------------------------------------

/** A `.tb-btn` with an inline icon and (optional) trailing text label. */
function tbBtn(opts: {
  iconName?: string
  label?: string
  title?: string
  cls?: string
  testid?: string
  id?: string
  onClick?: () => void
}): HTMLButtonElement {
  const b = el('button', 'tb-btn' + (opts.cls ? ' ' + opts.cls : ''))
  b.type = 'button'
  if (opts.title) b.title = opts.title
  if (opts.id) b.id = opts.id
  if (opts.testid) b.dataset.testid = opts.testid
  let html = ''
  if (opts.iconName) html += icon(opts.iconName)
  b.innerHTML = html
  if (opts.label) b.append(document.createTextNode(' ' + opts.label))
  if (opts.onClick) b.addEventListener('click', opts.onClick)
  return b
}

/** Vertical divider matching the live `w-px h-6 bg-slate-*` separators. */
function divider(tone = 'bg-slate-600'): HTMLElement {
  return el('div', `w-px h-6 ${tone} mx-1`)
}

/**
 * A dropdown button: returns a `position:relative` wrapper holding a `.tb-btn`
 * trigger (icon + label + chevron) and the `.dropdown` menu from ui/dom.ts.
 */
function dropBtn(opts: {
  iconName: string
  label?: string
  title?: string
  items: DropdownItem[]
  id?: string
  menuStyle?: Partial<CSSStyleDeclaration>
}): HTMLElement {
  const wrap = el('div', 'relative')
  if (opts.id) wrap.id = opts.id
  const btn = el('button', 'tb-btn')
  btn.type = 'button'
  if (opts.title) btn.title = opts.title
  btn.innerHTML = icon(opts.iconName)
  if (opts.label) btn.append(document.createTextNode(' ' + opts.label))
  btn.insertAdjacentHTML('beforeend', icon('chevronDown', 'w-3 h-3'))
  const menu = dropdown(btn, opts.items)
  if (opts.menuStyle) Object.assign(menu.style, opts.menuStyle)
  wrap.append(btn, menu)
  return wrap
}

// ---------------------------------------------------------------------------
// Header (snapshot L648-807)
// ---------------------------------------------------------------------------

let currentSizeLabelEl: HTMLElement | null = null
let landscapeBtn: HTMLButtonElement | null = null
let portraitBtn: HTMLButtonElement | null = null
let accountLabelEl: HTMLElement | null = null

/** Compose the size-picker label, e.g. "DYMO 99012 — 89 × 36 mm". */
function sizeLabelText(): string {
  const s = state.currentSize
  const w = state.orientation === 'portrait' ? s.heightMM : s.widthMM
  const h = state.orientation === 'portrait' ? s.widthMM : s.heightMM
  const prefix = s.brand ? `${s.brand} ${s.name}` : s.name
  return `${prefix} — ${w} × ${h} mm`
}

/** Refresh the header's size label + orientation active state. Safe to call anytime. */
export function refreshHeader(): void {
  if (currentSizeLabelEl) currentSizeLabelEl.textContent = sizeLabelText()
  landscapeBtn?.classList.toggle('active', state.orientation === 'landscape')
  portraitBtn?.classList.toggle('active', state.orientation === 'portrait')
  if (accountLabelEl) accountLabelEl.textContent = state.currentUser ? accountName() : 'Sign in'
}

function accountName(): string {
  const u = state.currentUser
  const email = (u?.email as string | undefined) ?? ''
  return email || 'Account'
}

function setOrientation(o: 'landscape' | 'portrait'): void {
  if (state.orientation === o) return
  state.orientation = o
  resizeCanvas()
  fitToView()
  updateStatus()
  refreshHeader()
}

export function buildHeader(): void {
  const host = document.getElementById('toolbar')
  if (!host) return
  host.innerHTML = ''
  host.className =
    'flex items-center h-11 bg-slate-900 border-b border-slate-800 px-3 gap-2 shrink-0 select-none'

  // Logo
  const logo = el('div', 'flex items-center mr-4 cursor-pointer')
  logo.innerHTML =
    `<span class="text-sm font-bold text-white tracking-tight">${logoHtml('text-brand')}</span>`
  logo.addEventListener('click', () => window.location.reload())
  host.append(logo)

  // Undo / Redo
  const histGroup = el('div', 'flex items-center gap-1 border-r border-slate-700 pr-3 mr-1')
  histGroup.append(
    tbBtn({ iconName: 'undo', title: 'Undo (Ctrl+Z)', id: 'btnUndo', onClick: () => void undo() }),
    tbBtn({ iconName: 'redo', title: 'Redo (Ctrl+Y)', id: 'btnRedo', onClick: () => void redo() }),
  )
  host.append(histGroup)

  // Size picker
  const sizeBtn = el('button', 'tb-btn border border-slate-600 rounded-md')
  sizeBtn.type = 'button'
  sizeBtn.id = 'btnSizePicker'
  sizeBtn.innerHTML = icon('sizePicker')
  currentSizeLabelEl = el('span', '', sizeLabelText())
  currentSizeLabelEl.id = 'currentSizeLabel'
  sizeBtn.append(currentSizeLabelEl)
  sizeBtn.insertAdjacentHTML('beforeend', icon('chevronDown', 'w-3 h-3 ml-1'))
  sizeBtn.addEventListener('click', () => openSizePicker())
  host.append(sizeBtn)

  // Orientation
  const oriGroup = el('div', 'flex items-center gap-1 border-l border-slate-700 pl-3 ml-1')
  landscapeBtn = tbBtn({ iconName: 'landscape', title: 'Landscape', id: 'btnLandscape', onClick: () => setOrientation('landscape') })
  portraitBtn = tbBtn({ iconName: 'portrait', title: 'Portrait', id: 'btnPortrait', onClick: () => setOrientation('portrait') })
  oriGroup.append(landscapeBtn, portraitBtn)
  host.append(oriGroup)

  // Spacer
  host.append(el('div', 'flex-1'))

  // Import Data / Batch
  host.append(
    tbBtn({
      iconName: 'importData',
      label: 'Import Data',
      title: 'Import data from Excel/CSV for batch labels',
      onClick: () => openBatchModal(),
    }),
  )

  host.append(divider())

  // New
  host.append(tbBtn({ iconName: 'plus', title: 'New design', onClick: () => openStartScreen() }))

  // Right-hand cluster (save / more / account / export / print)
  const right = el('div', 'flex items-center gap-1')

  // Save dropdown
  right.append(
    dropBtn({
      iconName: 'save',
      title: 'Save options',
      items: [
        { label: 'Cloud Save', iconHtml: icon('save', 'w-3.5 h-3.5'), onClick: () => void cloudSaveDesign() },
        { label: 'Save As', iconHtml: icon('saveAs', 'w-3.5 h-3.5'), onClick: () => void cloudSaveDesign() },
        { label: 'Download .olm', iconHtml: icon('download', 'w-3.5 h-3.5'), onClick: gated('olm-download', () => saveOlmDesign()) },
        { label: 'Open .olm', iconHtml: icon('open', 'w-3.5 h-3.5'), onClick: () => pickOlmFile() },
      ],
      menuStyle: { right: '0', left: 'auto', minWidth: '200px' },
    }),
  )

  right.append(divider('bg-slate-700'))

  // More menu (anon-style: signup / pricing / contact / api)
  right.append(
    dropBtn({
      iconName: 'more',
      title: 'More',
      id: 'moreMenuWrap',
      items: [
        { label: 'Create free account', iconHtml: icon('userPlus', 'w-3.5 h-3.5'), onClick: () => openAuth('signup') },
        { label: 'Pricing', iconHtml: icon('pricing', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.pricing, '_blank') },
        { label: 'Contact', iconHtml: icon('contact', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.contact, '_blank') },
        { label: 'API Docs', iconHtml: icon('apiDocs', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.apiDocs, '_blank') },
      ],
      menuStyle: { right: '0', left: 'auto', minWidth: '180px' },
    }),
  )

  // Account button + menu
  right.append(buildAccount())

  right.append(divider('bg-slate-700'))

  // Export (primary) + Print
  right.append(
    tbBtn({ iconName: 'export', label: 'Export', title: 'Export (Ctrl+E)', cls: 'primary', onClick: () => openExportModal() }),
    tbBtn({ iconName: 'print', label: 'Print', title: 'Print (Ctrl+P)', cls: 'primary', onClick: () => printLabel() }),
  )

  host.append(right)
  refreshHeader()
}

/** Build the `.user-btn` account control: opens auth when logged out, menu when in. */
function buildAccount(): HTMLElement {
  const wrap = el('div', 'relative')
  wrap.id = 'accountBtnWrap'

  const btn = el('button', 'user-btn')
  btn.type = 'button'
  btn.id = 'accountBtn'
  btn.innerHTML = icon('account')
  accountLabelEl = el('span', '', state.currentUser ? accountName() : 'Sign in')
  accountLabelEl.id = 'accountLabel'
  btn.append(accountLabelEl)

  // Account menu (signed-in): My Designs, settings, tier badge, sign out.
  const tier = (state.orgTier ?? 'free').toUpperCase()
  const menu = dropdown(btn, [
    { label: 'My Designs', iconHtml: icon('designs', 'w-3.5 h-3.5'), onClick: () => openDesignsModal() },
    { label: 'Account Settings', iconHtml: icon('settings', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.account, '_blank') },
    { label: 'Pricing', iconHtml: icon('pricing', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.pricing, '_blank') },
    { label: 'API Docs', iconHtml: icon('apiDocs', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.apiDocs, '_blank') },
    { label: 'Contact', iconHtml: icon('contact', 'w-3.5 h-3.5'), onClick: () => window.open(PRODUCT.links.contact, '_blank') },
    { label: `${tier} · Sign out`, iconHtml: icon('signOut', 'w-3.5 h-3.5'), onClick: () => void signOut() },
  ])
  menu.classList.add('user-menu')
  Object.assign(menu.style, { right: '0', left: 'auto' })

  // When signed out, the button must open auth directly (and never show the menu);
  // the dropdown() helper already toggles the menu on click, so intercept here.
  btn.addEventListener(
    'click',
    (e) => {
      if (!state.currentUser) {
        e.stopImmediatePropagation()
        menu.classList.add('hidden')
        openAuth('signin')
      }
    },
    true, // capture: run before dropdown()'s toggle listener
  )

  wrap.append(btn, menu)
  return wrap
}

// ---------------------------------------------------------------------------
// Secondary toolbar (snapshot L810-1027)
// ---------------------------------------------------------------------------

const SHAPE_ITEMS: { type: string; label: string }[] = [
  { type: 'rect', label: 'Rectangle' },
  { type: 'roundrect', label: 'Rounded Rect' },
  { type: 'circle', label: 'Circle' },
  { type: 'ellipse', label: 'Ellipse' },
  { type: 'triangle', label: 'Triangle' },
  { type: 'line', label: 'Horizontal Line' },
  { type: 'vline', label: 'Vertical Line' },
  { type: 'dline', label: 'Diagonal Line' },
  { type: 'diamond', label: 'Diamond' },
  { type: 'pentagon', label: 'Pentagon' },
  { type: 'hexagon', label: 'Hexagon' },
  { type: 'octagon', label: 'Octagon' },
  { type: 'star', label: 'Star' },
  { type: 'star4', label: '4-Point Star' },
  { type: 'heart', label: 'Heart' },
  { type: 'arrow', label: 'Arrow' },
  { type: 'cross', label: 'Cross / Plus' },
  { type: 'check', label: 'Checkmark' },
  { type: 'xmark', label: 'X Mark' },
  { type: 'warning', label: 'Warning' },
  { type: 'badge', label: 'Badge Circle' },
  { type: 'cloud', label: 'Cloud' },
  { type: 'bubble', label: 'Speech Bubble' },
]

const BARCODE_ITEMS: { fmt: string; label: string }[] = [
  { fmt: 'CODE128', label: 'Code 128' },
  { fmt: 'EAN13', label: 'EAN-13' },
  { fmt: 'EAN8', label: 'EAN-8' },
  { fmt: 'UPC', label: 'UPC-A' },
  { fmt: 'UPCE', label: 'UPC-E' },
  { fmt: 'CODE39', label: 'Code 39' },
  { fmt: 'CODE93', label: 'Code 93' },
  { fmt: 'ITF14', label: 'ITF-14' },
  { fmt: 'ITF', label: 'ITF (Interleaved 2 of 5)' },
  { fmt: 'codabar', label: 'Codabar' },
  { fmt: 'MSI', label: 'MSI' },
  { fmt: 'pharmacode', label: 'Pharmacode' },
]

let selectBtn: HTMLButtonElement | null = null
let insertFieldWrap: HTMLElement | null = null
let alignToolsEl: HTMLElement | null = null
let zoomLevelEl: HTMLElement | null = null

export function buildSecondaryToolbar(): void {
  const host = document.getElementById('toolbar2')
  if (!host) return
  host.innerHTML = ''
  host.className =
    'flex items-center h-10 bg-slate-800 border-b border-slate-700 px-3 gap-1 shrink-0 select-none'

  // Select tool
  selectBtn = tbBtn({
    iconName: 'select',
    label: 'Select',
    title: 'Select (V)',
    id: 'toolSelect',
    onClick: () => state.canvas?.discardActiveObject() && state.canvas?.requestRenderAll(),
  })
  selectBtn.classList.add('active')
  host.append(selectBtn)

  // Text
  host.append(tbBtn({ iconName: 'text', label: 'Text', title: 'Text (T)', onClick: () => addText() }))

  // Shapes dropdown
  host.append(
    dropBtn({
      iconName: 'shapes',
      label: 'Shape',
      title: 'Shape (R)',
      items: SHAPE_ITEMS.map((s) => ({ label: s.label, onClick: () => addShape(s.type as never) })),
      menuStyle: { minWidth: '200px', maxHeight: '420px', overflowY: 'auto' },
    }),
  )

  // Barcode dropdown
  host.append(
    dropBtn({
      iconName: 'barcode',
      label: 'Barcode',
      title: 'Barcode',
      items: BARCODE_ITEMS.map((b) => ({ label: b.label, onClick: () => void addBarcode(b.fmt) })),
      menuStyle: { minWidth: '200px', maxHeight: '420px', overflowY: 'auto' },
    }),
  )

  // QR
  host.append(tbBtn({ iconName: 'qrcode', label: 'QR', title: 'QR Code', onClick: () => void addQRCode() }))

  // Image (hidden file input)
  const imgInput = el('input') as HTMLInputElement
  imgInput.type = 'file'
  imgInput.accept = 'image/*'
  imgInput.style.display = 'none'
  imgInput.addEventListener('change', () => {
    const f = imgInput.files?.[0]
    if (f) void addImageFromFile(f)
    imgInput.value = ''
  })
  const imgBtn = tbBtn({ iconName: 'image', label: 'Image', title: 'Image', onClick: () => imgInput.click() })
  host.append(imgBtn, imgInput)

  // Table + Data Table (two separate buttons). "Data Table" inserts the
  // spreadsheet-style DynamicTable (header / template / summary rows) — an
  // invoice-profile tool; the label profile hides the button (API stays).
  host.append(tbBtn({ iconName: 'table', label: 'Table', title: 'Table', onClick: () => addTable(2, 2) }))
  if (PRODUCT.tools.includes('dataTable')) {
    host.append(tbBtn({ iconName: 'dataTable', label: 'Data Table', title: 'Data Table', onClick: () => addDynamicTable() }))
  }

  // Insert Field (hidden until batch data loaded; menu rebuilt on import/clear
  // via the 'olm:batchchange' event from batch/ui.ts)
  insertFieldWrap = dropBtn({
    iconName: 'field',
    label: 'Field',
    title: 'Insert data field onto label',
    id: 'insertFieldWrap',
    items: [],
    menuStyle: { minWidth: '200px', maxHeight: '300px', overflowY: 'auto' },
  })
  insertFieldWrap.classList.add('hidden')
  host.append(insertFieldWrap)
  if (!fieldMenuWired) {
    fieldMenuWired = true
    document.addEventListener('olm:batchchange', refreshInsertFieldMenu)
  }
  refreshInsertFieldMenu()

  host.append(divider())

  // Alignment / distribute / group tools — hidden until a selection exists.
  alignToolsEl = el('div', 'flex items-center gap-1 hidden')
  alignToolsEl.id = 'alignTools'
  alignToolsEl.append(
    tbBtn({ iconName: 'alignLeft', title: 'Align Left', onClick: () => alignObject('left') }),
    tbBtn({ iconName: 'alignCenter', title: 'Center Horizontal', onClick: () => alignObject('centerH') }),
    tbBtn({ iconName: 'alignRight', title: 'Align Right', onClick: () => alignObject('right') }),
    tbBtn({ iconName: 'alignCenterV', title: 'Center Vertical', onClick: () => alignObject('centerV') }),
    tbBtn({ iconName: 'distributeH', title: 'Distribute horizontally', id: 'distHBtn', onClick: () => distributeObjects('horizontal') }),
    tbBtn({ iconName: 'distributeV', title: 'Distribute vertically', id: 'distVBtn', onClick: () => distributeObjects('vertical') }),
    divider(),
    tbBtn({ iconName: 'group', title: 'Group (Ctrl+G)', id: 'groupBtn', onClick: () => groupSelected() }),
    tbBtn({ iconName: 'ungroup', title: 'Ungroup (Ctrl+Shift+G)', id: 'ungroupBtn', onClick: () => ungroupSelected() }),
    divider(),
    tbBtn({ iconName: 'duplicate', title: 'Duplicate (Ctrl+D)', onClick: () => void duplicateSelected() }),
    deleteBtn(),
  )
  host.append(alignToolsEl)

  host.append(el('div', 'flex-1'))

  // Zoom group
  const zoom = el('div', 'flex items-center gap-1')
  zoom.append(tbBtn({ iconName: 'zoomOut', title: 'Zoom Out', onClick: () => zoomOut() }))
  zoomLevelEl = el('span', 'text-xs text-slate-400 w-10 text-center', '100%')
  zoomLevelEl.id = 'zoomLevel'
  zoom.append(zoomLevelEl)
  zoom.append(tbBtn({ iconName: 'zoomIn', title: 'Zoom In', onClick: () => zoomIn() }))
  zoom.append(tbBtn({ label: 'Fit', title: 'Fit to View (0)', onClick: () => fitToView() }))
  host.append(zoom)

  // Keep the zoom % in sync with every zoom change (buttons, Fit, scroll-wheel).
  document.addEventListener('olm:zoom', (e) => {
    const f = (e as CustomEvent).detail ?? state.zoomFactor
    if (zoomLevelEl) zoomLevelEl.textContent = `${Math.round(f * 100)}%`
  })
}

function deleteBtn(): HTMLButtonElement {
  const b = tbBtn({ iconName: 'delete', title: 'Delete (Del)', onClick: () => deleteSelected() })
  b.style.color = '#f87171'
  return b
}

/** Show/hide the selection-only alignment tools (call from selection handler). */
export function setAlignToolsVisible(visible: boolean): void {
  alignToolsEl?.classList.toggle('hidden', !visible)
}

/** Show/hide the Insert-Field control (call when batch data is loaded/cleared). */
export function setInsertFieldVisible(visible: boolean): void {
  insertFieldWrap?.classList.toggle('hidden', !visible)
}

/** Rebuild the Field ▾ menu from the imported dataset's headers (live-editor
 *  parity: a "Data fields" list of {{field}} entries that insert onto canvas). */
let fieldMenuWired = false
function refreshInsertFieldMenu(): void {
  if (!insertFieldWrap) return
  const headers = state.batchHeaders ?? []
  insertFieldWrap.classList.toggle('hidden', headers.length === 0)
  const menu = insertFieldWrap.querySelector<HTMLElement>('.dropdown')
  if (!menu) return
  menu.replaceChildren()
  menu.append(el('div', 'px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500', 'Data fields'))
  for (const h of headers) {
    const row = el('div', 'dropdown-item', `{{${h}}}`)
    row.style.fontFamily = "ui-monospace, 'SF Mono', Menlo, monospace"
    row.style.color = '#60a5fa'
    row.addEventListener('click', () => {
      insertFieldOnCanvas(h)
      menu.classList.add('hidden')
    })
    menu.append(row)
  }
}

// ---------------------------------------------------------------------------
// Alignment / distribution / grouping helpers.
//
// The live editor exposed alignObject/distributeObjects/groupSelected/
// ungroupSelected globals (snapshot ~L4700+). The v2 widget/engine layer does
// not (yet) publish these as modules, so they are implemented inline here
// against the Fabric canvas. NOTE for follow-up: if a dedicated align/group
// module lands, replace these with imports.
// ---------------------------------------------------------------------------

function activeTargets() {
  const c = state.canvas
  if (!c) return { c: null, objs: [] as never[], active: null }
  const active = c.getActiveObject()
  if (!active) return { c, objs: [] as never[], active: null }
  // ActiveSelection exposes getObjects(); single object -> wrap in array.
  const objs = (active as { getObjects?: () => unknown[] }).getObjects?.() ?? [active]
  return { c, objs, active }
}

function alignObject(mode: 'left' | 'right' | 'centerH' | 'centerV'): void {
  const c = state.canvas
  if (!c) return
  const active = c.getActiveObject()
  if (!active) return
  // getBoundingRect() is SCENE-space; getWidth()/getHeight() are ELEMENT px
  // (scene × zoom) — divide by zoom or every target lands off-label (the bug
  // scaled with zoom level: at 228% "align right" overshot by 2.28×).
  const z = c.getZoom() || 1
  const w = c.getWidth() / z
  const h = c.getHeight() / z
  const b = active.getBoundingRect()
  switch (mode) {
    case 'left': active.set({ left: (active.left ?? 0) - b.left }); break
    case 'right': active.set({ left: (active.left ?? 0) + (w - (b.left + b.width)) }); break
    case 'centerH': active.set({ left: (active.left ?? 0) + (w / 2 - (b.left + b.width / 2)) }); break
    case 'centerV': active.set({ top: (active.top ?? 0) + (h / 2 - (b.top + b.height / 2)) }); break
  }
  active.setCoords()
  c.fire('object:modified', { target: active }) // history + autosave + panel sync
  c.requestRenderAll()
}

function distributeObjects(axis: 'horizontal' | 'vertical'): void {
  const { c, objs } = activeTargets()
  if (!c || objs.length < 3) return
  const items = (objs as { getBoundingRect: () => { left: number; top: number; width: number; height: number }; set: (o: object) => void; setCoords: () => void }[])
    .slice()
    .sort((a, z) =>
      axis === 'horizontal' ? a.getBoundingRect().left - z.getBoundingRect().left : a.getBoundingRect().top - z.getBoundingRect().top,
    )
  const first = items[0].getBoundingRect()
  const last = items[items.length - 1].getBoundingRect()
  const start = axis === 'horizontal' ? first.left : first.top
  const end = axis === 'horizontal' ? last.left : last.top
  const step = (end - start) / (items.length - 1)
  items.forEach((it, i) => {
    const r = it.getBoundingRect()
    if (axis === 'horizontal') it.set({ left: (it as unknown as { left: number }).left + (start + step * i - r.left) })
    else it.set({ top: (it as unknown as { top: number }).top + (start + step * i - r.top) })
    it.setCoords()
  })
  c.requestRenderAll()
}

// Fabric v7 removed ActiveSelection.toGroup / Group.toActiveSelection (v5 API);
// grouping is now: pull the objects out of the canvas, wrap in `new Group()`
// (which preserves their absolute transforms), and the reverse via removeAll().
export function groupSelected(): void {
  const c = state.canvas
  if (!c) return
  const active = c.getActiveObject()
  if (!active || active.type !== 'activeselection') return
  const objs = (active as ActiveSelection).getObjects()
  if (objs.length < 2) return
  c.discardActiveObject()
  objs.forEach((o) => c.remove(o))
  const group = new Group(objs)
  c.add(group)
  c.setActiveObject(group)
  c.fire('object:modified', { target: group })
  c.requestRenderAll()
}

export function ungroupSelected(): void {
  const c = state.canvas
  if (!c) return
  const active = c.getActiveObject()
  if (!active || active.type !== 'group') return
  const group = active as Group
  c.discardActiveObject()
  const children = group.removeAll() // restores absolute transforms
  c.remove(group)
  children.forEach((o) => c.add(o))
  const sel = new ActiveSelection(children, { canvas: c })
  c.setActiveObject(sel)
  c.fire('object:modified', { target: sel })
  c.requestRenderAll()
}

function pickOlmFile(): void {
  const input = el('input') as HTMLInputElement
  input.type = 'file'
  input.accept = '.olm,.json,application/json' // classic editor saved *.olm.json
  input.addEventListener('change', () => {
    const f = input.files?.[0]
    if (f) void openOlmDesign(f)
  })
  input.click()
}

function printLabel(): void {
  // Live behaviour: open the export modal (print routed through it) / window.print.
  try {
    openExportModal()
  } catch {
    gated('print', () => window.print())()
  }
}
